import { Helmet } from "react-helmet-async";
import { useState } from "react";
import Screen from "../Layouts/Screen/Screen";
import "./style.css"
import ItemList from "../../components/ItemList/ItemList";
import { Archive, Award, Bell, ChevronRight, DollarSign, FileText, HelpCircle, MessageSquare, Smartphone, Star, UserMinus, Users } from "react-feather";
import { Accessibility, AppWindow, BadgeCheck, Building2, Cable, CircleDollarSign, Languages, LayoutTemplate, LockKeyhole, MessageCircleQuestionMark, PersonStanding, ReceiptText, ShieldCheck, Text, Trash2, Sparkles } from "lucide-react";
import { LeftContent, RightContent } from "../../global-components/src/components/screen/left-content";
import { isIOSWebView, removeToNativeiOS } from "../../utils/platform";
import { Icon } from "../../global-components/src/components/icon";
import { useAuth, useMessage, useTheme } from "../../global-components/src";
import { useTranslation } from "react-i18next";
import { languages } from "../../global-components/src/i18n";
import { errorMessage } from "../../global-components/src/utils/text";
import { requestAccountDataExport } from "../../services/data-export";
import { canManageAccountSecurity } from "../../utils/account-security";

const Settings = () => {
    const { isDarkMode } = useTheme();
    const { user, token } = useAuth();
    const canManageSecurity = canManageAccountSecurity(user?.created_at);
    const { setMessage } = useMessage();
    const { t, i18n } = useTranslation();
    const currentMonth = new Date().getMonth();
    const isRetrospectiveSeason = currentMonth === 11 || currentMonth === 0;
    const canShowRetrospective = isRetrospectiveSeason || user?.stamps?.includes('brown');
    const [requestingDataExport, setRequestingDataExport] = useState(false);

    const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const lang = e.target.value;
        i18n.changeLanguage(lang);
        localStorage.setItem("language", lang);
    };

    const handleRequestDataExport = async () => {
        if (requestingDataExport) return;

        const title = t("settings.dataExport.title", { defaultValue: "Solicitar meus dados" });
        const description = t("settings.dataExport.confirmation", {
            defaultValue: "Enviaremos um arquivo ZIP com os dados associados à sua conta para {{email}}. Por segurança, senhas e tokens não são incluídos.",
            email: user?.email ?? "",
        });

        if (!window.confirm(`${title}

${description}`)) return;

        try {
            setRequestingDataExport(true);
            const { data } = await requestAccountDataExport(token);
            setMessage(
                data.message || t("settings.dataExport.success", { defaultValue: "Sua solicitação foi recebida. Verifique o e-mail da sua conta." }),
                "success",
            );
        } catch (err) {
            setMessage(errorMessage(err), "error");
        } finally {
            setRequestingDataExport(false);
        }
    };
    
    return (
        <>
            <Screen paddingTopTitle={true} title={t("settings.title")}>
                <Helmet>
                        <title>Paçoca - {t("settings.title")}</title>
                        <meta name="description" content={`Configure notificações, privacidade, sessões ativas doação, termos de uso, diretrizes e muito mais!`}/>
                        <meta name="keywords" content={`Paçoca, rede social brasileira, configurações, privacidade, termos de uso, doação, diretrizes, conexões, notificações, digitais}`}/>
                        <meta name="author" content="Paçoca Inc."/>
                        <meta property="og:title" content={`Configurações do Paçoca`}/>
                        <meta property="og:description" content={`Configure notificações, privacidade, sessões ativas doação, termos de uso, diretrizes e muito mais!`}/>
                        <meta property="og:image" content="/img/link-preview.png?v=1" />
                        <meta property="og:image:width" content="1200" />
                        <meta property="og:image:height" content="630" />
                        <meta property="og:url" content={window.location.href}/>
                        <meta property="og:type" content="website"/>
                        <meta name="twitter:card" content="summary_large_image"/>
                        <meta name="twitter:title" content={`Configurações do Paçoca`}/>
                        <meta name="twitter:description" content={`Configure notificações, privacidade, sessões ativas doação, termos de uso, diretrizes e muito mais!`}/>
                        <meta name="twitter:image" content="/img/link-preview.png?v=1" />
                </Helmet>
                
                {canManageSecurity && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/2fa">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="lock">
                            <LockKeyhole className="icon-settings" />
                        </Icon> {t("settings.2fa.title")}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}
                
                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/privacidade">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="encrypted">
                        <ShieldCheck className="icon-settings" /> 
                    </Icon> {t("settings.privacy.title")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList
                    className="user-select-none"
                    bigPaddingHorizontalNotification
                    onClick={handleRequestDataExport}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleRequestDataExport();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-disabled={requestingDataExport}
                    style={{ cursor: requestingDataExport ? "wait" : "pointer" }}
                >
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="download">
                        <Archive className="icon-settings" />
                    </Icon> {requestingDataExport
                        ? t("settings.dataExport.requesting", { defaultValue: "Solicitando dados..." })
                        : t("settings.dataExport.title", { defaultValue: "Solicitar meus dados" })}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/acessibilidade">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="accessibility_new">
                        <PersonStanding className="icon-settings" /> 
                    </Icon> {t("settings.accessibility")} 
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification>
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="language">
                        <Languages className="icon-settings" />
                    </Icon>

                    {t("settings.language")}

                   <div className="col" style={{ paddingLeft: 20 }}>
                     <select
                        className="form-control language"
                        value={i18n.language}
                        onChange={handleLanguageChange}
                        style={{
                            marginLeft: "auto",
                            background: "transparent",
                            border: "none",
                            color: isDarkMode ? "#fff" : "#000",
                            fontSize: 14,
                            cursor: "pointer"
                        }}
                        >
                            {languages.map((lang) => (
                                <option key={lang.code} value={lang.code}>
                                    {i18n.t(lang.key)}
                                </option>
                            ))}
                        </select>
                   </div>
                </ItemList>


                {canManageSecurity && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/meus-dispositivos">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="devices">
                            <Smartphone className="icon-settings" />
                        </Icon> {t("settings.myDevices")}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/configuracoes/notificacoes">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="notification_sound">
                        <Bell className="icon-settings" />
                    </Icon> {t("settings.notifications")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                {canShowRetrospective && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/retrospectiva">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="celebration">
                            <Sparkles className="icon-settings" />
                        </Icon> {t("retrospective.menuTitle")}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                {user?.stamps?.includes("brown") && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/moderador">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="admin_panel_settings">
                            <ShieldCheck className="icon-settings" />
                        </Icon> Ações de moderação
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                {user?.stamps?.includes("black") && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/configuracoes/respostas-rapidas">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="quick_phrases">
                            <MessageSquare className="icon-settings" />
                        </Icon> Respostas Rápidas do Chat
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                {!removeToNativeiOS(user?.id) && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/verificado">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="verified">
                            <BadgeCheck className="icon-settings" /> 
                        </Icon> {t("settings.verified")}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                {!removeToNativeiOS(user?.id) && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/doacao">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="paid">
                            <CircleDollarSign className="icon-settings" /> 
                        </Icon> {t("settings.donate")}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/suporte">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="contact_support">
                        <MessageCircleQuestionMark className="icon-settings" /> 
                    </Icon> {t("settings.support")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>


                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/termos-uso">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="contract">
                        <FileText className="icon-settings" /> 
                    </Icon> {t("settings.termsUse")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/politica-privacidade">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="privacy">
                        <ShieldCheck className="icon-settings" />
                    </Icon> {t("settings.privacyPolicy")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/diretrizes">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="policy">
                        <Archive className="icon-settings" /> 
                    </Icon> {t("settings.guidelines")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/politica-seguranca-infantil">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="account_child">
                        <ReceiptText className="icon-settings" /> 
                    </Icon> {t("settings.childSafetyPolicy")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                {!removeToNativeiOS(user?.id) && !user?.is_verified_company && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/empresas/solicitar-selo">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="apartment">
                            <Building2 className="icon-settings" /> 
                        </Icon> {t("settings.companySeal")}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                {!removeToNativeiOS(user?.id) && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/meus-selos">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="editor_choice">
                            <Award className="icon-settings" /> 
                        </Icon> {t("meaningSeals.myStamps")}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                {!removeToNativeiOS(user?.id) && (
                    <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/significado-selos">
                        <Icon className="menu-father-icon" invertColor={!isDarkMode} name="editor_choice">
                            <Award className="icon-settings" /> 
                        </Icon> {t("settings.meaningSeals")}
                        {/* <Award className="icon-settings"/> Significado dos Selos */}
                        <ChevronRight className="chevron-right"/>
                    </ItemList>
                )}

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/blog">
                    <Icon className="menu-father-icon" invertColor={!isDarkMode} name="news">
                        <Text style={{ marginRight: 10 }} className="icon-settings" /> 
                    </Icon> {t("settings.blog")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/design">
                    <AppWindow style={{ marginRight: 10 }} className="icon-settings"/> {t("settings.design")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/conta">
                    <span style={{ color: "red" }}>
                        <Trash2 style={{ marginRight: 10 }}/> {t("settings.deleteAccount")}
                    </span>
                    <ChevronRight className="chevron-right"/>
                </ItemList>
            </Screen>
        </>
    );
};

export default Settings;